/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { css } from "styled-components";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import translate from "@insite/client-framework/Translate";


const enum fields {
    modalTitle = "modalTitle",
    variant = "variant",
    label = "label",
    alignment = "alignment",
    textColor = "textColor",
    buttonColor = "buttonColor",
    marginLeft = "marginLeft",
    marginRight = "marginRight",
    btnMinWidth = "btnMinWidth",
    borderWidth = "buttonType",

    youtubeVideoId = "youtubeVideoId",
    autoplay = "autoplay",
    mute = "mute",
    height = "height"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.alignment]: string;
        [fields.textColor]: string;
        [fields.buttonColor]: string;
        [fields.marginLeft]: string;
        [fields.marginRight]: string;
        [fields.btnMinWidth]: string;
        [fields.modalTitle]: String;

        [fields.youtubeVideoId]: string;
        [fields.autoplay]: string;
        [fields.mute]: string;
        [fields.height]: string;
    };
}

type Props = HasHistory & OwnProps;

const CmsButton: React.FunctionComponent<Props> = ({ fields, id }: Props) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const modalCloseHandler = () => setIsModalOpen(false);

    const {alignment, label, variant, textColor, buttonColor, marginLeft, marginRight, btnMinWidth, modalTitle, 
        youtubeVideoId, autoplay, mute, height
    } = fields;

    const wrapperStyles = {
        css: css`
            width: 100%;
            display: flex;
            justify-content: ${alignment ?? "left"};
        `,
    };

    const buttonStyles = {
        css: css`
            color: ${textColor};
            border: 2px solid ${buttonColor};
            z-index: 2;
            margin-left:${marginLeft}%;
            maring-right:${marginRight}%;
            min-width:${btnMinWidth}%;   
        `
    }

    const gridItemStyles = {
            css: css`
                padding: 0;
                min-width: 100%;
            `
    }

    const videoWrapperStypes = {
        css: css`
            width: 100%;
            height: ${height};
            .iframeDiv {
                border: none; 
                width: 100%; 
                height: 100%;
            }
        `
    }

    return (
        <StyledWrapper {...wrapperStyles}>
            <Button color={buttonColor} {...buttonStyles} variant={variant} onClick={() => setIsModalOpen(true)}>
                {label}
            </Button>

            <Modal
                headline={translate(`${modalTitle}`)}
                contentLabel={'test'}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
                >
                <GridContainer>
                    <GridItem {...gridItemStyles} >
                    <StyledWrapper {...videoWrapperStypes}>
                        <iframe className="iframeDiv" src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=${autoplay}&mute=${mute}`}>
                        </iframe>
                    </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CmsButton,
    definition: {
        group: "Basic",
        icon: "Button",
        fieldDefinitions: [
            {
                name: fields.modalTitle,
                displayName: "Modal Title",
                editorTemplate: "TextField",
                fieldType: "General",
                tooltip: "title tip",
                defaultValue: '',
                placeholder: "text placeholder",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.youtubeVideoId,
                displayName: "Youtube Video Id",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.height,
                displayName: "Video height (px, rem)",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.autoplay,
                displayName: "Autoplay",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "True",
                        value: "1",
                    },
                    {
                        displayName: "False",
                        value: "0",
                    },
                ],
                tooltip: "drop down tip",
                isRequired: true,
                defaultValue: "false",
            },
            {
                fieldType: "General",
                name: fields.mute,
                displayName: "Mute",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "True",
                        value: "1",
                    },
                    {
                        displayName: "False",
                        value: "0",
                    },
                ],
                tooltip: "drop down tip",
                isRequired: true,
                defaultValue: "false",
            },
            {
                fieldType: "General",
                name: fields.buttonColor,
                editorTemplate: "ColorPickerField",
                displayName: "Button Color",
                defaultValue: "#f16906",
            },
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "white",
            },
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.marginLeft,
                displayName: "Button Margin Left (%)",
                editorTemplate: "TextField",
                defaultValue: "0%",
                fieldType: "Translatable",
            },
            {
                name: fields.marginRight,
                displayName: "Button Margin Right (%)",
                editorTemplate: "TextField",
                defaultValue: "0%",
                fieldType: "Translatable",
            },
            {
                name: fields.btnMinWidth,
                displayName: "Button Min Width (%)",
                editorTemplate: "TextField",
                defaultValue: "10%",
                fieldType: "Translatable",
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
